/**
 * Bibliothek — Download & Druck (Fragen-PDFs, kompletter Kurs, Trainer-Material).
 * NotebookLM wurde 2026-06-11 auf Wunsch des Auftraggebers komplett entfernt.
 *
 * Die Download-Listen sind DATENGETRIEBEN aus dem Kurs-Manifest (Feld `download`
 * je Modul, stabiler id-basierter PDF-Name) — analog Web renderDownloads().
 * Hardcodierte Arrays fuehrten frueher zu 4 toten Links (V2/C1).
 */
import { useEffect, useState } from "preact/hooks";
import { downloadUrl } from "../data/assets";
import { isTrainer } from "../state/session";
import { activeCourseId, loadCourse } from "../data/course-store";
import type { Course, CourseModule } from "../data/types";
import { IconDownload } from "../app/icons";

interface Dl {
  file: string;
  title: string;
  sub: string;
  featured?: boolean;
}

const KURS: Dl[] = [
  { file: "Sentinus-KI-Kurs_Kompletter-Kurs.pdf", title: "Kompletter Kurs · als PDF", sub: "Alle Module mit vollständigen Inhalten — zum Lesen & Drucken", featured: true },
];

function fragenAnzahl(m: CourseModule): number {
  return m.chapters.reduce((a, c) => a + (c.questionCount || 0), 0);
}
function modulLabel(m: CourseModule): string {
  return m.menueNr != null ? `Modul ${m.menueNr} · ${m.title}` : m.title;
}
function modulDl(m: CourseModule): Dl {
  const kap = m.chapters.length;
  return {
    file: m.download || "",
    title: modulLabel(m),
    sub: `${kap} Kapitel · ${fragenAnzahl(m)} Fragen`,
  };
}
function baueListen(course: Course): { fragen: Dl[]; trainer: Dl[] } {
  const mitPdf = (m: CourseModule) => !!m.download && fragenAnzahl(m) > 0;
  const teil = course.modules.filter((m) => m.group === "teilnehmer" && mitPdf(m));
  const train = course.modules.filter((m) => m.group === "trainer" && mitPdf(m));
  const nummern = teil.map((m) => m.menueNr).filter((n) => n != null).join(", ");
  return {
    fragen: [
      { file: "Sentinus-KI-Kurs_Alle-Fragen.pdf", title: "Alle Fragen · Gesamt-PDF", sub: `Module ${nummern} — zum Ausdrucken`, featured: true },
      ...teil.map(modulDl),
    ],
    trainer: train.map(modulDl),
  };
}

function DlList({ items }: { items: Dl[] }) {
  return (
    <div class="stack" style="gap:14px">
      {items.map((d) => (
        <a key={d.file} class={`glass dl${d.featured ? " dl-featured" : ""}`} href={downloadUrl(d.file)} target="_blank" rel="noopener">
          <span class="ic"><IconDownload size={18} /></span>
          <span>
            <span class="t">{d.title}</span>
            <span class="s">{d.sub}</span>
          </span>
        </a>
      ))}
    </div>
  );
}

export function Library() {
  const [listen, setListen] = useState<{ fragen: Dl[]; trainer: Dl[] }>({ fragen: [], trainer: [] });

  useEffect(() => {
    loadCourse(activeCourseId.value).then((c) => setListen(baueListen(c))).catch(() => {});
  }, []);

  return (
    <div class="stack">
      <div>
        <h2 class="section-title">Bibliothek</h2>
        <p class="section-sub">Fragen &amp; Kurs als PDF — zum Lesen, Drucken und Üben.</p>
      </div>

      <div class="glass notice">
        <span class="ico">●</span>
        <p>
          <strong>Lieber auf Papier?</strong> Lade die Fragen herunter und übe mit ihnen bei
          jeder beliebigen KI.
        </p>
      </div>

      <h3 class="group-label">Die Fragen</h3>
      <DlList items={listen.fragen} />

      <h3 class="group-label">Kompletter Kurs</h3>
      <DlList items={KURS} />

      {isTrainer.value && listen.trainer.length ? (
        <>
          <h3 class="group-label trainer">Für Trainer &amp; Einrichtung</h3>
          <DlList items={listen.trainer} />
        </>
      ) : null}
    </div>
  );
}
